import xor from 'lodash/xor.js';
import {
  STATUS_ACTIVE_FROM_BILL_DAY,
  STATUS_ACTIVE_TILL_BILL_DAY,
  STATUS_PROTECTION_PERIOD,
} from '../constants.js';

/**
 * @property {LoveGroup[]} groups
 */
export default class Love {
  /**
   * @param {LoveGroup[]} groups
   */
  constructor({ groups }) {
    this.groups = groups;
  }

  /**
   * @returns {LoveGroup[]}
   */
  get activeGroups() {
    return this.groups.filter(group => group.status !== STATUS_ACTIVE_FROM_BILL_DAY);
  }

  /**
   * @returns {boolean}
   */
  get hasLove17Grow() {
    return this.groups.some(group => group.isLove17Grow);
  }

  /**
   * @returns {LoveGroup[]}
   */
  get love17GrowGroups() {
    return this.groups.filter(group => group.isLove17Grow);
  }

  /**
   * @returns {LoveGroup[]}
   */
  get love17GrowActiveGroups() {
    return this.love17GrowGroups.filter(
      group => ![STATUS_ACTIVE_FROM_BILL_DAY, STATUS_PROTECTION_PERIOD].includes(group.status),
    );
  }

  /**
   * @returns {boolean}
   */
  get hasOnlyLove17() {
    return this.groups.every(group => group.isLove17);
  }

  /**
   * @returns {boolean}
   */
  get hasGroupInProtectionPeriod() {
    return this.groups.some(group => group.isInProtectionPeriod);
  }

  /**
   * @returns {boolean}
   */
  get hasChangeOnBillDay() {
    return (
      this.love17GrowGroups.some(group => group.status === STATUS_ACTIVE_TILL_BILL_DAY) ||
      this.love17GrowGroups.some(group => group.status === STATUS_ACTIVE_FROM_BILL_DAY)
    );
  }

  /**
   * @returns {string[][]}
   */
  get subscriptionIdCurrentGroupsRepresentation() {
    return this.groups.map(group => group.currentMemberSubscriptionIds);
  }

  /**
   * @returns {string[][]}
   */
  get subscriptionIdPotentialGroupsRepresentation() {
    return this.groups.map(group => group.potentialMemberSubscriptionIds);
  }

  /**
   * @param {string} subscriptionId
   * @returns {boolean}
   */
  getIsSubscriptionIdInSomeActiveGroup(subscriptionId) {
    return this.activeGroups.some(group => group.getIsSubscriptionIdInCurrentGroup(subscriptionId));
  }

  /**
   * @param {string} subscriptionId
   * @returns {LoveGroup[]}
   */
  getActiveGroupsBySubscriptionId(subscriptionId) {
    return this.activeGroups.filter(group =>
      group.getIsSubscriptionIdInCurrentGroup(subscriptionId),
    );
  }

  /**
   * @param {string} subscriptionId
   * @returns {boolean}
   */
  getIsSubscriptionIdInMultipleGroups(subscriptionId) {
    return (
      this.groups.filter(group => group.getIsSubscriptionIdInPotentialGroup(subscriptionId))
        .length > 1
    );
  }

  /**
   * Checks whether current Love groups are the same as provided Love groups,
   * e.g. whether they contain same amount of groups with same combination of members.
   *
   * @param {Love} otherLove
   * @returns {boolean}
   */
  isSameAs(otherLove) {
    if (this.groups.length !== otherLove.groups.length) {
      return false;
    }
    return otherLove.subscriptionIdCurrentGroupsRepresentation.every(aGroup =>
      this.subscriptionIdPotentialGroupsRepresentation.some(
        bGroup => xor(aGroup, bGroup).length === 0,
      ),
    );
  }
}
